import {
  Button,
  Col,
  Divider,
  Dropdown,
  HasAccess,
  MultiLineEllipsisText,
  PriorityBadge,
  Row,
  Space,
  Tag,
  Typography,
} from 'components';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { DownOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { AssignmentEditModal } from './assignmentEditModal';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import { EditIcon, EditIconButton } from 'components/common/icons';
import { OrderStatusBadge } from 'components/common/StatusBadge/OrderStatusBadge';
import { AllowedStatusTransitions, OrderRejectionReasons, OrderStatusType } from 'models/Order';
import { ContactPersons } from './contactPersons';
import { RejectModal } from './rejectModal';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';

const { Text, Title } = Typography;

export const Details = React.memo(() => {
  const {
    orderDetails,
    isLoading,
    isFinalStatus,
    handleStatusChange,
    onAssignmentOpen,
    assignmentModalState,
    onAssignmentCancel,
    handleModalCancel,
    form,
    handleRejectionConfirm,
    isRejectionModalVisible,
  } = useDetails();

  const changeStatusMenu = useMemo(() => {
    const currentStatus = orderDetails.status as OrderStatusType;
    if (!currentStatus) return [];
    const availableOptions = AllowedStatusTransitions[currentStatus] || [];
    return availableOptions.map((status) => ({
      label: status,
      key: status,
    }));
  }, [orderDetails.status]);

  if (isLoading) {
    return (
      <Row gutter={[0, 20]}>
        <Col span={24}>Loading...</Col>
      </Row>
    );
  }
  return (
    <>
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row align={'middle'} wrap={false} gutter={4}>
                <Col flex="0 1 auto">
                  <Text type={'secondary'}>Reference Number: </Text>
                  {orderDetails.referenceNumber ? (
                    <Link
                      target={'_blank'}
                      to={`${ADMIN_ROUTES.deals.view}/${orderDetails.referenceId}/purchase-request`}
                      className={'underline-button'}
                    >
                      <Text underline type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                        {' '}
                        {orderDetails.referenceNumber}
                      </Text>
                    </Link>
                  ) : (
                    'Not Available'
                  )}
                </Col>
              </Row>
              <Row wrap={false} align={'middle'} className={'gap-4'} justify={'space-between'}>
                <Col flex="0 1 auto">
                  <Title level={4} ellipsis={{ tooltip: true }} className={'font-weight-800 reset-title'}>
                    Order Id: {orderDetails.uuid}
                  </Title>
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.orders.order.write}>
                    <Button type={'link'} icon={<EditIconButton />} disabled={true} />
                  </HasAccess>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[8, 8]} align={'middle'}>
                <Col>
                  <OrderStatusBadge type={orderDetails.status} />
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.orders.order.write}>
                    <Dropdown
                      menu={{
                        items: changeStatusMenu.map((item) => ({
                          label: <OrderStatusBadge type={item.key as OrderStatusType} />,
                          key: item.key,
                        })),
                        onClick: ({ key }) => handleStatusChange(key as OrderStatusType),
                      }}
                      trigger={['click']}
                    >
                      <Button disabled={isFinalStatus || !changeStatusMenu?.length} type={'primary'}>
                        <Space>
                          Change Status
                          <DownOutlined />
                        </Space>
                      </Button>
                    </Dropdown>
                  </HasAccess>
                </Col>
              </Row>
            </Col>
            {orderDetails?.rejectionReason && (
              <Col span={24} style={{ marginTop: 8 }}>
                <Text type={'secondary'} ellipsis={{ tooltip: true }} style={{ fontSize: 16 }}>
                  {convertEnumsToReadableStrings(orderDetails?.rejectionReason)}
                </Text>
              </Col>
            )}
            {orderDetails?.comments && (
              <Col span={24} style={{ marginBottom: 8 }}>
                <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                  {orderDetails?.comments}
                </Text>
              </Col>
            )}
            <Col span={24}>
              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                Client Request Number: {orderDetails.clientRequestNumber || 'Not Available'}
              </Text>
            </Col>
            <Col span={24}>
              <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} ellipsis={{ tooltip: true }}>
                Source: {orderDetails.source || 'Not Available'}
              </Text>
            </Col>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Text style={{ color: '#99A0AE' }}>{orderDetails.formattedCreatedDate}</Text>
                <Text style={{ color: '#99A0AE' }}>{orderDetails.formattedLastUpdated}</Text>
              </Row>
            </Col>
            <Col span={12}>
              <Row align={'middle'} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <Text type={'secondary'} className={'font-size-12'}>
                    CREATED BY
                  </Text>
                </Col>
                <Text ellipsis={{ tooltip: true }}>
                  {orderDetails.createdBy?.firstName || ''} {orderDetails.createdBy?.lastName || ''}
                </Text>
              </Row>
            </Col>
            <Col span={12}>
              <Row align={'middle'} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <Text type={'secondary'} className={'font-size-12'}>
                    LAST UPDATED BY
                  </Text>
                </Col>
                <Text ellipsis={{ tooltip: true }}>
                  {orderDetails.lastModifiedBy?.firstName || ''} {orderDetails.lastModifiedBy?.lastName || ''}
                </Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Divider className={styles.divider} />
        <Col span={24}>
          <Row gutter={[0, 20]} align={'middle'}>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'}>
                <Col>
                  <Text type={'secondary'} className={'font-size-16 font-weight-800'}>
                    Total Amount
                  </Text>
                </Col>
                <Col>
                  <Row align={'middle'} className={'gap-4'}>
                    <Text type={'secondary'} className={'font-size-16 font-weight-800'}>
                      {orderDetails.total ? `€ ${orderDetails.total}` : 'Not Set'}
                    </Text>
                    {orderDetails.margin && (
                      <Col className={styles.total_margin}>
                        <Text style={{ color: '#1FC16B' }} className={'font-size-12 font-weight-600'}>
                          {orderDetails.margin}%
                        </Text>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <Row align={'middle'} justify={'space-between'}>
                    <Col>
                      <Text className={'font-weight-600 font-size-16'}>Assignments</Text>
                    </Col>
                    <Col>
                      <HasAccess role={PERMISSIONS.orders.order.write}>
                        {!isFinalStatus && (
                          <Button icon={<EditIcon />} type="text" size={'small'} onClick={onAssignmentOpen} />
                        )}
                      </HasAccess>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Purchase Manager
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.purchaseManager
                              ? ` ${orderDetails.purchaseManager.firstName} ${orderDetails.purchaseManager.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            PM Assistant
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.purchaseManagerAssistant
                              ? ` ${orderDetails.purchaseManagerAssistant.firstName} ${orderDetails.purchaseManagerAssistant.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Sales Manager
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.salesManager
                              ? ` ${orderDetails.salesManager.firstName} ${orderDetails.salesManager.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            SM Assistant
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.salesManagerAssistant
                              ? ` ${orderDetails.salesManagerAssistant.firstName} ${orderDetails.salesManagerAssistant.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Post-Sales Manager
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.postSalesManager
                              ? ` ${orderDetails.postSalesManager.firstName} ${orderDetails.postSalesManager.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={24}>
                      <Row gutter={[12, 12]} justify={'space-between'} wrap={false}>
                        <Col flex="none">
                          <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                            Post-SM Assistant
                          </Text>
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                          <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                            {orderDetails.postSalesManagerAssistant
                              ? ` ${orderDetails.postSalesManagerAssistant.firstName} ${orderDetails.postSalesManagerAssistant.lastName}`
                              : ' Not Set'}
                          </Text>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Divider className={styles.divider} />
        <Col span={24}>
          <Row gutter={[0, 6]}>
            <Col span={24} className={'margin-b-6'}>
              <Typography.Text className={'font-weight-600 font-size-16'} style={{ color: '#05796B' }}>
                Client Information
              </Typography.Text>
            </Col>
            <Col span={24}>
              <Row gutter={4} align={'middle'} wrap={false}>
                <Col flex="0 1 auto">
                  <Link
                    target={'_blank'}
                    to={`${ADMIN_ROUTES.client}/${orderDetails.client?.id}/general`}
                    className={'underline-button'}
                  >
                    <Text underline className={'font-weight-600'} type={'secondary'} ellipsis={{ tooltip: true }}>
                      {orderDetails.client?.name}
                    </Text>
                  </Link>
                </Col>
                <Col>
                  <Text className={styles.option_tag} ellipsis={{ tooltip: true }}>
                    {orderDetails.client?.clientType}
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <PriorityBadge
                type={orderDetails.client?.priorityLevel}
                tooltip={orderDetails.client?.priorityRationale}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={6}>
            {orderDetails.client?.tags?.map((_tag: string, index: number) => (
              <Col key={index + _tag}>
                <Tag title={_tag} className={'custom-tag'}>
                  {_tag}
                </Tag>
              </Col>
            ))}
          </Row>
        </Col>
        <Col span={24}>
          <MultiLineEllipsisText
            maxLines={3}
            subTitle={'Additional Notes:'}
            text={orderDetails?.client?.additionalNotes}
          />
        </Col>
        <ContactPersons />
      </Row>
      <AssignmentEditModal open={assignmentModalState} onCancel={onAssignmentCancel} />
      <RejectModal
        form={form}
        open={isRejectionModalVisible}
        onSubmit={handleRejectionConfirm}
        onCancel={handleModalCancel}
        rejectionReasons={Object.values(OrderRejectionReasons)}
      />
    </>
  );
});
